import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { UserGroupService } from '@core/services/user-group.service';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';

interface IAdminUserGroupState {
  userGroups: IUserGroup[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: IAdminUserGroupState = {
  error: null,
  loading: false,
  userGroups: null,
};

@Injectable()
export class AdminUserGroupStore extends ComponentStore<IAdminUserGroupState> {
  public readonly userGroups$ = this.selectSignal((state: IAdminUserGroupState) => state.userGroups);
  public readonly loading$ = this.selectSignal((state: IAdminUserGroupState) => state.loading);
  public readonly error$ = this.selectSignal((state: IAdminUserGroupState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchUserGroups = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(() =>
        this.userGroupService.getCurrentUserGroups(new HttpParams().set('limit', '1000').set('order_by', 'name')).pipe(
          tap((userGroups: IUserGroup[]) => {
            this.patchState({ loading: false, userGroups });
          }),
          this.handleError('Failed to load user groups'),
        ),
      ),
    );
  });

  constructor(private readonly userGroupService: UserGroupService) {
    super(initialState);
  }
}
